import { useDisclosure } from '@mantine/hooks';
import { debounce, noop } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ChevronDownIcon, ChevronUpIcon } from 'src/svgs/Viagogo';
import { ListingWithComp } from 'src/tiles/ListingTilesV2';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionInfo } from 'src/WebApiController';

import * as styles from './InventoryListingSortingContent.css';
import { useSectionAndZoneListingCounts } from './ListingSectionFilterContent.hooks';

export const ListingSectionFilterContent = ({
  selectedSections,
  sections,
  inventory,
  onUpdateSelectedSections,
  onClose,
}: {
  selectedSections: string[];
  sections: SectionInfo[];
  inventory: ListingWithComp[];
  onUpdateSelectedSections: (_: string[]) => void;
  onClose: () => void;
}) => {
  const [searchText, setSearchText] = useState<string>();
  const [localSelectedSections, setLocalSelectedSections] =
    useComputedState<string[]>(selectedSections);

  const { zoneSections, zoneListingCount, sectionListingCount } =
    useSectionAndZoneListingCounts(inventory, sections);

  const [openZones, { toggle: toggleZones }] = useDisclosure(true);
  const [openSections, { toggle: toggleSections }] = useDisclosure(true);

  const { filteredSections, filteredZones } = useMemo(() => {
    const filteredZones = Object.keys(zoneSections).filter(
      (zone) =>
        !searchText || zone.toLowerCase().startsWith(searchText.toLowerCase())
    );
    const filteredSections = Object.values(zoneSections)
      .flatMap((sections) => sections)
      .filter(
        (section) =>
          !searchText ||
          section.toLowerCase().startsWith(searchText.toLowerCase())
      )
      .toSorted();

    return {
      filteredZones,
      filteredSections,
    };
  }, [searchText, zoneSections]);

  const selectedZones = useMemo(() => {
    return Object.keys(zoneSections).filter((zone) => {
      const sections = zoneSections[zone];
      return sections.every((section) =>
        localSelectedSections.includes(section)
      );
    });
  }, [localSelectedSections, zoneSections]);

  const onToggleZoneSelect = useCallback(
    (zone: string) => {
      if (selectedZones.includes(zone)) {
        const sectionsInZone = zoneSections[zone] ?? [];
        const updated = localSelectedSections.filter(
          (section) => !sectionsInZone.includes(section)
        );
        setLocalSelectedSections(updated);
      } else {
        const sectionsInZone = zoneSections[zone] ?? [];
        const updated = Array.from(
          new Set([...sectionsInZone, ...localSelectedSections])
        );
        setLocalSelectedSections(updated);
      }
    },
    [
      localSelectedSections,
      selectedZones,
      setLocalSelectedSections,
      zoneSections,
    ]
  );

  const onToggleSectionSelect = useCallback(
    (section: string) => {
      if (localSelectedSections.includes(section)) {
        const updated = localSelectedSections.filter((s) => s !== section);
        setLocalSelectedSections(updated);
      } else {
        setLocalSelectedSections([...localSelectedSections, section]);
      }
    },
    [localSelectedSections, setLocalSelectedSections]
  );

  const onAddFilter = useCallback(() => {
    onUpdateSelectedSections(localSelectedSections ?? []);
    onClose();
  }, [localSelectedSections, onClose, onUpdateSelectedSections]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack direction="column" width="full" gap="s">
        <span className={styles.sortAndFilterHeader}>
          <Content id={ContentId.AllFilters} />
        </span>
        <PosSearchBox
          width="100%"
          style={{ height: '30px' }}
          onSearchChange={debounce(setSearchText, 200)}
          rootProps={{ shape: 'square' }}
        />
      </Stack>
      <Stack
        direction="column"
        width="full"
        gap="s"
        style={{ flex: 1, overflowY: 'auto' }}
      >
        <Stack
          width="full"
          direction="column"
          className={styles.inventoryFilterGroupItem}
          style={{ cursor: 'pointer' }}
          gap="l"
        >
          <Stack
            role="button"
            width="full"
            alignItems="center"
            justifyContent="spaceBetween"
            onClick={toggleZones}
          >
            <Content id={ContentId.Zones} />
            {openZones ? (
              <ChevronUpIcon size={vars.iconSize.s} />
            ) : (
              <ChevronDownIcon size={vars.iconSize.s} />
            )}
          </Stack>
          {openZones && (
            <Stack direction="column" gap="m">
              {filteredZones.map((zone) => {
                return (
                  <Stack
                    key={zone}
                    alignItems="center"
                    justifyContent="spaceBetween"
                    onClick={() => onToggleZoneSelect(zone)}
                  >
                    <Checkbox
                      checked={selectedZones.includes(zone)}
                      onChange={noop}
                      label={zone}
                      color={vars.color.textBrand}
                      labelPosition="right"
                      checkboxSizeVariant="light"
                    />
                    <span className={styles.sortAndFilterSubheader}>
                      {(zoneListingCount[zone] ?? 0).toLocaleString()}
                    </span>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
        <Stack
          width="full"
          direction="column"
          className={styles.inventoryFilterGroupItem}
          style={{ cursor: 'pointer' }}
          gap="l"
        >
          <Stack
            role="button"
            width="full"
            alignItems="center"
            justifyContent="spaceBetween"
            onClick={toggleSections}
          >
            <Content id={ContentId.Sections} />
            {openSections ? (
              <ChevronUpIcon size={vars.iconSize.s} />
            ) : (
              <ChevronDownIcon size={vars.iconSize.s} />
            )}
          </Stack>
          {openSections && (
            <Stack direction="column" gap="m">
              {filteredSections.map((section) => {
                return (
                  <Stack
                    key={section}
                    alignItems="center"
                    justifyContent="spaceBetween"
                    onClick={() => onToggleSectionSelect(section)}
                  >
                    <Checkbox
                      checked={localSelectedSections.includes(section)}
                      onChange={() => onToggleSectionSelect(section)}
                      label={section}
                      color={vars.color.textBrand}
                      labelPosition="right"
                      checkboxSizeVariant="light"
                    />

                    <span className={styles.sortAndFilterSubheader}>
                      {(sectionListingCount[section] ?? 0).toLocaleString()}
                    </span>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={() => setLocalSelectedSections([])}
        >
          <Content id={ContentId.Clear} />
        </Button>
        <Button style={{ height: '40px' }} shape="rect" onClick={onAddFilter}>
          <Content id={ContentId.AddSections} />
        </Button>
      </Stack>
    </Stack>
  );
};
